/**
 * @fileoverview Data of the read-only teacher assessment drawer (AR-898).
 *
 * - Scores: `GET /api/session_meetings/:lessonId/list_assessment_scores`
 *   (paginated; every page is fetched so the drawer never needs infinite scroll).
 * - Journal: `GET /session_meetings/:lessonId/student_assessment/:assessmentId`
 *   (`master_journal`). The policy behind it requires an `approved` project, so a
 *   finished project answers 401 — the journal is then simply omitted.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { getListAssessmentScores } from 'common/hooks/useAssessmentScoreListHook'
import { api } from 'helpers'
import type { TeacherAssessmentData, TeacherScoreItem } from './types'

const HTTP_OK = 200
const FIRST_PAGE = 1
/** Safety net against a pagination that never reports `isLastPage`. */
const MAX_PAGES = 50

type ScoreKind = 'content' | 'skill'

/** The page shape we rely on (camelCased by the API helper). */
interface ScoreListPage {
  result?: unknown[]
  pagination?: { isLastPage?: boolean; nextPage?: number | null }
}

async function fetchAllScores(lessonId: string, studentId: string, kind: ScoreKind): Promise<TeacherScoreItem[]> {
  const items: TeacherScoreItem[] = []
  let page: number | null = FIRST_PAGE
  for (let guard = 0; page && guard < MAX_PAGES; guard += 1) {
    const response = await getListAssessmentScores({
      urlId: lessonId,
      params: { student_id: studentId, type: kind, page },
      type: 'assessment',
    })
    const data = response.data as ScoreListPage | undefined
    items.push(...((data?.result ?? []) as TeacherScoreItem[]))
    page = data?.pagination && !data.pagination.isLastPage ? data.pagination.nextPage ?? null : null
  }
  return items
}

async function fetchTeacherJournal(lessonId: string, assessmentId: string | null): Promise<string | null> {
  if (!assessmentId) return null
  try {
    const response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.studentAssessment(lessonId, assessmentId))
    if (response.status !== HTTP_OK) return null
    const journal = (response.data as { masterJournal?: string } | null)?.masterJournal ?? ''
    return journal.trim() ? journal : ''
  } catch {
    return null
  }
}

export async function fetchTeacherAssessment(
  lessonId: string,
  studentId: string,
  assessmentId: string | null,
): Promise<TeacherAssessmentData> {
  const [standards, skills, journal] = await Promise.all([
    fetchAllScores(lessonId, studentId, 'content'),
    fetchAllScores(lessonId, studentId, 'skill'),
    fetchTeacherJournal(lessonId, assessmentId),
  ])
  return { standards, skills, journal }
}
